import React, { useMemo, useState } from 'react';
import { Box, Typography, Avatar, IconButton, Tooltip } from '@mui/material';
import { Reply as ReplyIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MessageBubbleProps } from '../../../types/chat';
import { format, formatDistanceToNowStrict } from 'date-fns';
import ReplyToDisplay from './ReplyToDisplay';
import MessageReactions from './MessageReactions';
import { useChatParticipant } from '../../../contexts/ChatParticipantContext';

interface ExtendedMessageBubbleProps extends MessageBubbleProps {
  onReactionPress?: (emoji: string) => void;
  onReplyPress?: (message: any) => void;
  onDeletePress?: (message: any) => void;
  onImagePress?: (imageUrl: string, message?: any) => void;
  currentUserId?: string;
  currentUserType?: string;
}

const MessageBubble: React.FC<ExtendedMessageBubbleProps> = React.memo(({
  message,
  isCurrentUser,
  children,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  onImagePress,
  currentUserId,
  currentUserType
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { clientAvatarUrl } = useChatParticipant();

  const bubbleStyles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
      mb: 0.1, // Extremely tight margin bottom
      px: 2,
      py: 0.1, // Extremely reduced vertical padding
      width: '100%', // Ensure full width
    },
    messageRow: {
      display: 'flex',
      flexDirection: 'row' as const,
      alignItems: 'flex-start', // Changed from flex-end to flex-start
      maxWidth: '80%', // Increased from 70% to give more space
      gap: 1,
      width: '100%', // Ensure full width utilization
    },
    bubble: {
      px: 1.5, // Reduced horizontal padding
      py: 0.5, // Further reduced vertical padding for extremely compact bubbles
      borderRadius: 3,
      backgroundColor: isCurrentUser ? '#2563EB' : '#F1F3F4',
      color: isCurrentUser ? '#ffffff' : '#000000',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
      wordBreak: 'break-word' as const,
      maxWidth: '100%',
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 0.1, // Extremely reduced margin bottom
      opacity: 0.7,
    },
    timestamp: {
      fontSize: '0.75rem',
      color: '#6B7280',
      mt: 0.1, // Extremely reduced margin top
      opacity: 0.7,
    }
  };

  const showAvatar = !isCurrentUser;
  const showUserName = !isCurrentUser;

  const messageDate = useMemo(() => {
    const rawDate = message?.createdOn;
    if (!rawDate) {
      return null;
    }

    if (rawDate instanceof Date && !Number.isNaN(rawDate.getTime())) {
      return rawDate;
    }

    if (typeof rawDate === 'number' || typeof rawDate === 'string') {
      const parsed = new Date(rawDate);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof rawDate === 'object') {
      // Firestore Timestamp
      if (typeof (rawDate as any).toDate === 'function') {
        const converted = (rawDate as any).toDate();
        return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
      }

      if ('seconds' in (rawDate as Record<string, unknown>)) {
        const seconds = Number((rawDate as any).seconds);
        const nanos = Number((rawDate as any).nanoseconds ?? 0);
        if (!Number.isNaN(seconds)) {
          return new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
        }
      }
    }

    return null;
  }, [message?.createdOn]);

  const formattedTimestamp = useMemo(() => {
    if (!messageDate) {
      return '';
    }

    const absolute = format(messageDate, 'M/d/yyyy h:mm a');
    const relative = formatDistanceToNowStrict(messageDate, { addSuffix: true });

    return `${absolute} (${relative})`;
  }, [messageDate]);
  
  // Show delete button if user is a coach (can delete any message) or if it's their own message
  // Temporarily allow deletion when userType is undefined (likely coach users)
  const canDeleteMessage = currentUserType === 'coach' || 
                           currentUserType === 'admin' || 
                           message.fromUID === currentUserId ||
                           currentUserType === undefined; // Temporary: allow when userType is undefined

  return (
    <Box 
      className="message-hover" // Add hover class for reaction button visibility
      sx={bubbleStyles.container}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* User info (name) - only for other users */}
      {showUserName && (
        <Box sx={bubbleStyles.userInfo}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280' }}>
            {message.fromUser.firstName} {message.fromUser.lastName}
          </Typography>
        </Box>
      )}
      
      {/* Message row with avatar and bubble */}
      <Box sx={{
        ...bubbleStyles.messageRow,
        flexDirection: isCurrentUser ? 'row-reverse' : 'row'
      }}>
        {/* Avatar - only for other users */}
        {showAvatar && (
          <Avatar
            src={message.fromUser.avatarUrl || clientAvatarUrl}
            sx={{ 
              width: 32, 
              height: 32,
              border: '2px solid rgba(0, 119, 200, 0.2)'
            }}
          >
            {message.fromUser.firstName.charAt(0)}{message.fromUser.lastName.charAt(0)}
          </Avatar>
        )}
        
        {/* Message bubble container with reply button */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          maxWidth: '100%',
          position: 'relative'
        }}>
          {/* Reply and Delete buttons - appear on hover */}
          {isHovered && (onReplyPress || (canDeleteMessage && onDeletePress)) && (
            <Box sx={{
              position: 'absolute',
              top: -8,
              right: isCurrentUser ? undefined : -40,
              left: isCurrentUser ? -40 : undefined,
              zIndex: 10,
              display: 'flex',
              gap: 0.5,
            }}>
              {onReplyPress && (
                <Tooltip title="Reply to message">
                  <IconButton
                    size="small"
                    onClick={() => onReplyPress(message)}
                    sx={{
                      backgroundColor: '#ffffff',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      },
                      width: 32,
                      height: 32,
                    }}
                  >
                    <ReplyIcon sx={{ fontSize: 16, color: '#666' }} />
                  </IconButton>
                </Tooltip>
              )}
              
              {canDeleteMessage && onDeletePress && (
                <Tooltip title="Delete message">
                  <IconButton
                    size="small"
                    onClick={() => onDeletePress(message)}
                    sx={{
                      backgroundColor: '#ffffff',
                      border: '1px solid rgba(211, 47, 47, 0.2)',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        backgroundColor: '#ffebee',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                        borderColor: 'rgba(211, 47, 47, 0.4)',
                      },
                      width: 32,
                      height: 32,
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          
          {/* Message bubble */}
          <Box sx={bubbleStyles.bubble}>
            {/* Reply-to display */}
            {message.replyTo && (
              <ReplyToDisplay 
                replyTo={message.replyTo} 
                isCurrentUser={isCurrentUser} 
                onImagePress={onImagePress}
              />
            )}
            
            {/* Message content */}
            {children}
          </Box>
          
          {/* Reactions - only show if there are existing reactions */}
          {onReactionPress && currentUserId && (message.reactions && message.reactions.length > 0) && (
            <MessageReactions
              reactions={message.reactions || []}
              currentUserId={currentUserId}
              onReactionPress={onReactionPress}
              isCurrentUser={isCurrentUser}
              isHovered={isHovered}
            />
          )}
        </Box>
      </Box>
      
      {/* Timestamp */}
      <Typography 
        variant="caption" 
        sx={{
          ...bubbleStyles.timestamp,
          textAlign: isCurrentUser ? 'right' : 'left',
          alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
        }}
      >
        {formattedTimestamp}
      </Typography>

      {/* Reactions - show add button below timestamp when no reactions exist */}
      {onReactionPress && currentUserId && (!message.reactions || message.reactions.length === 0) && (
        <MessageReactions
          reactions={message.reactions || []}
          currentUserId={currentUserId}
          onReactionPress={onReactionPress}
          isCurrentUser={isCurrentUser}
          isHovered={isHovered}
        />
      )}
    </Box>
  );
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
